import { ref, unref, type ComputedRef } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useFiltersStore } from "@/stores/filters";
import { parseSearchQuery, updateSearchQuery } from "@/helpers/filters";
import type { SearchRequest } from "@/types/search/SearchRequest";

/** Keys that are non-filter query params used by EntityDetailPage and should be preserved when syncing filters to the URL. */
const PRESERVED_QUERY_KEYS = [
	"shareId",
	"artifactId",
	"artifactType",
	"openSpark",
	"pendingImages",
	"pendingDocs",
	"pendingVideos",
	"from",
	"fromSearch",
	"searchState"
];

/** Keys that represent user-applied filter overrides (as opposed to structural/base request params). */
const FILTER_FIELDS: (keyof SearchRequest)[] = [
	"searchTerm",
	"contentTypes",
	"statuses",
	"customerIds",
	"userIds",
	"createdByIds",
	"labels",
	"startDate",
	"endDate",
	"dateFilter",
	"orderBy",
	"orderByDescending",
	"organization",
	"latitude",
	"longitude"
];

/**
 * Composable that provides filter state persistence for entity detail pages.
 *
 * Reads/writes filter overrides to both a Pinia store (runtime cache) and URL query params (durable).
 * The URL is the source of truth — the store prevents re-parsing the URL on every internal operation.
 */
export function useFilterPersistence(options: {
	pageKey: ComputedRef<string>;
	routeName: ComputedRef<string> | string;
	buildBaseRequest: (reload?: boolean) => SearchRequest;
}) {
	const router = useRouter();
	const route = useRoute();
	const filtersStore = useFiltersStore();

	const userFilters = ref<Partial<SearchRequest>>({});
	const filtersVisible = ref(false);

	/**
	 * Build a merged SearchRequest: base defaults + user filter overrides.
	 * User filters take precedence over base values.
	 */
	function buildRequest(reload: boolean = false): SearchRequest {
		const base = options.buildBaseRequest(reload);
		const overrides = userFilters.value;

		return {
			...base,
			...overrides,
			searchTerm: overrides.searchTerm ?? base.searchTerm
		} as SearchRequest;
	}

	/**
	 * Restore filter state from URL query params (on page load / back-nav).
	 * Falls back to the Pinia store cache if URL has no filter params.
	 */
	function restoreFromUrl(): void {
		userFilters.value = {};
		filtersVisible.value = false;

		const query = route.query as Record<string, string>;
		const parsedFilters = parseSearchQuery(query);

		const allowedKeys = new Set<string>(FILTER_FIELDS);

		for (const key of Object.keys(parsedFilters)) {
			if (!allowedKeys.has(key)) {
				delete parsedFilters[key as keyof SearchRequest];
			}
		}

		const hasUrlFilters = Object.keys(parsedFilters).some(
			(key) => !PRESERVED_QUERY_KEYS.includes(key) && parsedFilters[key as keyof SearchRequest] != null
		);

		if (hasUrlFilters) {
			userFilters.value = parsedFilters;
			filtersStore.setFilters(options.pageKey.value, parsedFilters);
		} else {
			const cached = filtersStore.getFilters(options.pageKey.value);

			if (cached) {
				userFilters.value = cached.filters;
			}
		}

		// Auto-show the filter panel when any non-search filter is active so users can see what's applied.
		// Why: on mobile the panel is gated by v-if, so a refresh would hide active filters otherwise.
		const hasNonSearchFilters = Object.keys(userFilters.value).some(
			(key) => key !== "searchTerm" && userFilters.value[key as keyof SearchRequest] != null
		);

		const cached = filtersStore.getFilters(options.pageKey.value);

		if (hasNonSearchFilters) {
			filtersVisible.value = true;
		} else if (cached) {
			filtersVisible.value = cached.filtersVisible;
		}
	}

	/**
	 * Handle a filter change from the Filters component.
	 * Extracts user-applied filter overrides, persists to store, and syncs to URL.
	 */
	function onFilterChange(updatedRequest: SearchRequest): void {
		const overrides: Partial<SearchRequest> = {};

		for (const field of FILTER_FIELDS) {
			const value = updatedRequest[field];

			if (value != null && value !== "" && !(Array.isArray(value) && value.length === 0)) {
				(overrides as any)[field] = value;
			}
		}

		if (!overrides.searchTerm && userFilters.value.searchTerm) {
			overrides.searchTerm = userFilters.value.searchTerm;
		}

		userFilters.value = overrides;
		filtersStore.setFilters(options.pageKey.value, overrides, filtersVisible.value);
		// Sync only the user filter overrides — buildRequest() would also serialize structural
		// base-request params (parentIds, projectId, entitySearch, orderBy, etc.) into the URL.
		syncToUrl(userFilters.value as SearchRequest);
	}

	/**
	 * Handle search term change.
	 */
	function onSearchTermChange(searchTerm: string): void {
		if (searchTerm) {
			userFilters.value = { ...userFilters.value, searchTerm };
		} else {
			const { searchTerm: _, ...rest } = userFilters.value;
			userFilters.value = rest;
		}

		filtersStore.setFilters(options.pageKey.value, userFilters.value, filtersVisible.value);
		syncToUrl(userFilters.value as SearchRequest);
	}

	/**
	 * Update filter visibility and persist to store.
	 */
	function setFiltersVisible(visible: boolean): void {
		filtersVisible.value = visible;
		filtersStore.setFiltersVisible(options.pageKey.value, visible);
	}

	/**
	 * Sync the current filter state to URL query params, preserving non-filter params.
	 */
	function syncToUrl(searchRequest: SearchRequest): void {
		if (!router) {
			return;
		}

		const routeName = unref(options.routeName);

		if (routeName && router.currentRoute?.value?.name !== routeName) {
			return;
		}

		const currentQuery = router.currentRoute?.value?.query ?? {};

		// Preserve non-filter query params
		const preserved: Record<string, string> = {};

		for (const key of PRESERVED_QUERY_KEYS) {
			if (currentQuery[key]) {
				preserved[key] = currentQuery[key] as string;
			}
		}

		updateSearchQuery(router, searchRequest, routeName || undefined, preserved);
	}

	return {
		userFilters,
		filtersVisible,
		buildRequest,
		restoreFromUrl,
		onFilterChange,
		onSearchTermChange,
		setFiltersVisible
	};
}
